/**
 * Theme Loader System
 *
 * Responsible for initializing and applying themes at runtime.
 * Sets window variables and injects CSS variables for the active theme.
 */

import { getActiveTheme, getThemeConfigByName, isValidTheme } from './themeConfig'
import { injectCSSVariables } from './cssInjection'

/**
 * Initialize the theme system
 *
 * Call this once at application startup (e.g., in main.js or App.vue created)
 * Sets up window variables and CSS variables based on VUE_APP_THEME env var.
 */
export function initializeTheme() {
  const themeName = getActiveTheme()
  applyTheme(themeName)
}

/**
 * Apply a specific theme
 *
 * Sets all window variables and CSS variables for the given theme.
 *
 * @param themeName - The theme to apply
 * @throws Error if theme name is invalid
 */
export function applyTheme(themeName) {
  if (!isValidTheme(themeName)) {
    throw new Error(`Invalid theme name: ${themeName}`)
  }

  const config = getThemeConfigByName(themeName)

  // Set theme identifier
  window.VUE_APP_THEME = themeName

  // Set branding variables
  window.SCHOOL_LOGO = config.logoPath
  window.LOCATION_NAME = config.brandName

  // Set color variables on window (for components that read directly)
  window.HEADER_COLOR = config.colors.headerColor
  window.HEADER_HOVER = config.colors.headerHover
  window.BUTTON_COLOR = config.colors.buttonColor
  window.BUTTON_HOVER = config.colors.buttonHover
  // Primary action bg - defaults to buttonColor if not specified (for contrast)
  window.PRIMARY_ACTION_BG = config.colors.primaryActionBg || config.colors.buttonColor
  window.PRIMARY_ACTION_BG_HOVER = config.colors.primaryActionBgHover || config.colors.buttonHover
  window.FRAME_COLOR = config.colors.frameColor
  window.HIGHLIGHT_BUTTON_COLOR = config.colors.highlightButtonColor
  window.HIGHLIGHT_BUTTON_HOVER = config.colors.highlightButtonHover
  window.HEADER_BUTTON_COLOR = config.colors.headerButtonColor
  window.TEXT_COLOR = config.colors.textColor
  window.LIGHT_TEXT_COLOR = config.colors.lightTextColor
  window.BUTTON_TEXT_COLOR = config.colors.buttonTextColor
  window.HEADER_TEXT_COLOR = config.colors.headerTextColor
  window.HEADER_BUTTON_TEXT_COLOR = config.colors.headerButtonTextColor
  window.ALERT_COLOR = config.colors.alertColor
  window.ALERT_HOVER = config.colors.alertHover
  window.SUCCESS_COLOR = config.colors.successColor
  window.SUCCESS_HOVER = config.colors.successHover
  window.INACTIVE_COLOR = config.colors.inactiveColor

  // Inject CSS variables for stylesheet usage
  injectCSSVariables(themeName)
}

/**
 * Get all current theme window variables
 */
export function getWindowVars() {
  return {
    VUE_APP_THEME: window.VUE_APP_THEME,
    SCHOOL_LOGO: window.SCHOOL_LOGO,
    LOCATION_NAME: window.LOCATION_NAME,
    HEADER_COLOR: window.HEADER_COLOR,
    HEADER_HOVER: window.HEADER_HOVER,
    BUTTON_COLOR: window.BUTTON_COLOR,
    BUTTON_HOVER: window.BUTTON_HOVER,
    PRIMARY_ACTION_BG: window.PRIMARY_ACTION_BG,
    PRIMARY_ACTION_BG_HOVER: window.PRIMARY_ACTION_BG_HOVER,
    FRAME_COLOR: window.FRAME_COLOR,
    HIGHLIGHT_BUTTON_COLOR: window.HIGHLIGHT_BUTTON_COLOR,
    HIGHLIGHT_BUTTON_HOVER: window.HIGHLIGHT_BUTTON_HOVER,
    HEADER_BUTTON_COLOR: window.HEADER_BUTTON_COLOR,
    TEXT_COLOR: window.TEXT_COLOR,
    LIGHT_TEXT_COLOR: window.LIGHT_TEXT_COLOR,
    BUTTON_TEXT_COLOR: window.BUTTON_TEXT_COLOR,
    HEADER_TEXT_COLOR: window.HEADER_TEXT_COLOR,
    HEADER_BUTTON_TEXT_COLOR: window.HEADER_BUTTON_TEXT_COLOR,
    ALERT_COLOR: window.ALERT_COLOR,
    ALERT_HOVER: window.ALERT_HOVER,
    SUCCESS_COLOR: window.SUCCESS_COLOR,
    SUCCESS_HOVER: window.SUCCESS_HOVER,
    INACTIVE_COLOR: window.INACTIVE_COLOR
  }
}

/**
 * Get the currently applied theme name
 */
export function getCurrentTheme() {
  return window.VUE_APP_THEME
}
